import axios from 'axios';
import { getMicrocredentialsTaskModule, MICROCREDENTIALS_TASK_MODES } from '../config/microcredentialsTaskModules';

function resolveModuleConfig(mode = MICROCREDENTIALS_TASK_MODES.PERFORMANCE) {
   return getMicrocredentialsTaskModule(mode);
}

function appendIfPresent(formData, key, value) {
   if (value !== undefined && value !== null && value !== '') {
      formData.append(key, value);
   }
}

function toPerformanceTaskFormData(payload = {}, filesForUpload = [], mode = MICROCREDENTIALS_TASK_MODES.PERFORMANCE) {
   const moduleConfig = resolveModuleConfig(mode);
   const formData = new FormData();

   appendIfPresent(formData, 'Course_id', payload.course_id ?? payload.courseId ?? payload.Course_id);
   appendIfPresent(formData, 'Topic_id', payload.topic_id ?? payload.topicId ?? payload.Topic_id);
   appendIfPresent(formData, 'Title', payload.title ?? payload.Title);
   appendIfPresent(formData, 'Description', payload.description ?? payload.Description);
   appendIfPresent(formData, 'Document', payload.document ?? payload.Document);
   appendIfPresent(formData, 'Removed_documents', payload.removed_documents ?? payload.Removed_documents);
   appendIfPresent(formData, 'Created_by', payload.created_by ?? payload.createdBy ?? payload.Created_by);
   appendIfPresent(formData, 'Max_points', payload.max_points ?? payload.maxPoints ?? payload.Max_points);
   appendIfPresent(formData, 'Plagiarism_checker', payload.plagiarism_checker ?? payload.plagiarismChecker ?? payload.Plagiarism_checker ?? false);
   appendIfPresent(formData, 'Task_type', payload.task_type ?? payload.taskType ?? payload.Task_type ?? moduleConfig.taskType);

   filesForUpload.forEach((file) => {
      if (file) {
         formData.append('Files', file.originFileObj || file);
      }
   });

   return formData;
}

function toPerformanceTaskAssignmentPayload(payload = {}, mode = MICROCREDENTIALS_TASK_MODES.PERFORMANCE) {
   const moduleConfig = resolveModuleConfig(mode);
   const assignmentId = payload.mcpta_id ?? payload.id ?? payload.assignment_id ?? payload.assignmentId ?? '';
   const performanceTaskId =
      payload.mcpt_id ??
      payload.Mcpt_id ??
      payload.performance_task?.mcpt_id ??
      payload.performanceTask?.mcpt_id ??
      payload.performance_task_id ??
      payload.performanceTaskId ??
      null;

   if (!performanceTaskId) {
      throw new Error(moduleConfig.label + ' assignment requires a valid mcpt_id.');
   }

   const finalPayload = {
      mcpt_id: performanceTaskId,
      assigned_students: payload.assigned_students ?? payload.assignedStudents ?? '',
      assigned_by: payload.assigned_by ?? payload.assignedBy ?? payload.created_by ?? payload.createdBy ?? null,
      submission_date: payload.submission_date ?? payload.submissionDate ?? payload.submit_date ?? payload.end_date ?? null,
      level: payload.level ?? payload.level_id ?? payload.levelId ?? null,
      Task_type: payload.task_type ?? payload.taskType ?? payload.Task_type ?? moduleConfig.taskType,
   };

   if (assignmentId) {
      finalPayload.mcpta_id = assignmentId;
   }

   return finalPayload;
}

export async function listPerformanceTasks(params = {}, mode = MICROCREDENTIALS_TASK_MODES.PERFORMANCE) {
   const moduleConfig = resolveModuleConfig(mode);
   const response = await axios.get(moduleConfig.teacherBase, {
      params: {
         courseId: params.courseId ?? params.course_id ?? null,
         topicId: params.topicId ?? params.topic_id ?? null,
         createdBy: params.createdBy ?? params.created_by ?? null,
         task_type: params.task_type ?? params.taskType ?? moduleConfig.taskType,
      },
   });
   return response.data;
}

export async function createPerformanceTask(payload, filesForUpload = [], mode = MICROCREDENTIALS_TASK_MODES.PERFORMANCE) {
   const moduleConfig = resolveModuleConfig(mode);
   const response = await axios.post(
      moduleConfig.teacherBase,
      toPerformanceTaskFormData(payload, filesForUpload, mode),
      { headers: { 'Content-Type': 'multipart/form-data' } }
   );
   return response.data;
}

export async function updatePerformanceTask(mcptId, payload, filesForUpload = [], mode = MICROCREDENTIALS_TASK_MODES.PERFORMANCE) {
   const moduleConfig = resolveModuleConfig(mode);
   const response = await axios.put(
      `${moduleConfig.teacherBase}/${mcptId}`,
      toPerformanceTaskFormData(payload, filesForUpload, mode),
      {
         headers: { 'Content-Type': 'multipart/form-data' },
         params: {
            task_type: payload?.task_type ?? payload?.taskType ?? payload?.Task_type ?? moduleConfig.taskType,
         },
      }
   );
   return response.data;
}

export async function deletePerformanceTask(id, mode = MICROCREDENTIALS_TASK_MODES.PERFORMANCE) {
   const moduleConfig = resolveModuleConfig(mode);
   const response = await axios.delete(`${moduleConfig.teacherBase}/${id}`, {
      params: { task_type: moduleConfig.taskType },
   });
   return response.data;
}

export async function duplicatePerformanceTask(params = {}, mode = MICROCREDENTIALS_TASK_MODES.PERFORMANCE) {
   const moduleConfig = resolveModuleConfig(mode);
   const response = await axios.post(`${moduleConfig.teacherBase}/duplicate`, null, {
      params: {
         mcptId: params.mcptId ?? params.mcpt_id ?? params.id ?? null,
         created_by: params.created_by ?? params.createdBy ?? null,
         task_type: params.task_type ?? params.taskType ?? moduleConfig.taskType,
      },
   });
   return response.data;
}

export async function listDuplicatedPerformanceTasks(params = {}, mode = MICROCREDENTIALS_TASK_MODES.PERFORMANCE) {
   const moduleConfig = resolveModuleConfig(mode);
   const response = await axios.get(`${moduleConfig.teacherBase}/duplicatedlist`, {
      params: {
         created_by: params.created_by ?? params.createdBy ?? null,
         task_type: params.task_type ?? params.taskType ?? moduleConfig.taskType,
      },
   });
   return response.data;
}

export async function listPerformanceTaskAssignments(params = {}, mode = MICROCREDENTIALS_TASK_MODES.PERFORMANCE) {
   const moduleConfig = resolveModuleConfig(mode);
   const temporal = params.temporal ?? params.t ?? 'current';
   const useAllAssigned = Boolean(params.useAllAssigned);

   if (useAllAssigned) {
      const response = await axios.get(`${moduleConfig.teacherBase}/listallassigned`, {
         params: {
            t: temporal,
            task_type: params.task_type ?? params.taskType ?? moduleConfig.taskType,
         },
      });
      return response.data;
   }

   const response = await axios.get(`${moduleConfig.teacherBase}/listassigned`, {
      params: {
         assigned_by: params.assigned_by ?? params.created_by ?? params.assignedBy ?? params.createdBy,
         t: temporal,
         task_type: params.task_type ?? params.taskType ?? moduleConfig.taskType,
      },
   });
   return response.data;
}

export async function getPerformanceTaskAssignment(id, mode = MICROCREDENTIALS_TASK_MODES.PERFORMANCE) {
   const moduleConfig = resolveModuleConfig(mode);
   const response = await axios.get(`${moduleConfig.teacherBase}/assigninfo`, {
      params: {
         id,
         task_type: moduleConfig.taskType,
      },
   });
   return response.data;
}

export async function createPerformanceTaskAssignment(payload, mode = MICROCREDENTIALS_TASK_MODES.PERFORMANCE) {
   const moduleConfig = resolveModuleConfig(mode);
   const response = await axios.post(`${moduleConfig.teacherBase}/assign`, toPerformanceTaskAssignmentPayload(payload, mode));
   return response.data;
}

export async function updatePerformanceTaskAssignment(payload, mode = MICROCREDENTIALS_TASK_MODES.PERFORMANCE) {
   const moduleConfig = resolveModuleConfig(mode);
   const response = await axios.post(`${moduleConfig.teacherBase}/assignupdate`, toPerformanceTaskAssignmentPayload(payload, mode));
   return response.data;
}
